// Web checkout pricing shown on the landing and plan pages.
// ANNUAL_PRICE must match the Stripe price behind REACT_APP_STRIPE_PRICE_ID.
export const ANNUAL_PRICE = 39.99;
export const TRIAL_DAYS = 7;

export type PromoDetails = {
  percentOff: number | null;
  amountOff: number | null; // cents
  duration: "once" | "repeating" | "forever" | null;
};

export type PromoCheck =
  | { valid: true; details: PromoDetails | null }
  | { valid: false; message: string };

export const formatPrice = (amount: number) => `$${amount.toFixed(2)}`;

export const discountedPrice = (promo: PromoDetails) =>
  Math.max(
    0,
    promo.percentOff
      ? ANNUAL_PRICE * (1 - promo.percentOff / 100)
      : ANNUAL_PRICE - (promo.amountOff || 0) / 100
  );

export const discountLabel = (promo: PromoDetails) =>
  promo.percentOff
    ? `${promo.percentOff}% off`
    : promo.amountOff
      ? `${formatPrice(promo.amountOff / 100)} off`
      : "";

// "for year one, $39.99/year after" unless the coupon applies to every renewal
export const discountTerm = (promo: PromoDetails) =>
  promo.duration === "forever" ? "/year" : ` for year one, ${formatPrice(ANNUAL_PRICE)}/year after`;

export const checkPromoCode = async (code: string): Promise<PromoCheck> => {
  const res = await fetch(
    `${process.env.REACT_APP_API_URL}/api/orders/validatePromoCode?code=${encodeURIComponent(code)}`
  );
  const data = await res.json();
  if (!data?.valid) return { valid: false, message: data?.message || "Invalid coupon code." };
  const details =
    data.percentOff || data.amountOff
      ? { percentOff: data.percentOff ?? null, amountOff: data.amountOff ?? null, duration: data.duration ?? null }
      : null;
  return { valid: true, details };
};
